import DeleteIcon from '@mui/icons-material/Delete';
import { Divider, IconButton, Stack, Typography, Box, Snackbar } from '@mui/material';
import PaiActionIconText from 'features/ui/base-components/pai-action-icon-text';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiSwitch from 'features/ui/base-components/pai-switch';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { AutoReplyCase } from '../../campaign-builder.types';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import { testAutoreply } from 'API/linkedin-funnel';
import { useUserData } from 'hooks/use-user-data';
import { useState } from 'react';
import { ProductGroups } from 'enums';

/** Props for AutoReplySettings */
interface AutoReplySettingsProps {
  loading: boolean;

  /** Is the auto-reply active or not? */
  isActive: boolean;

  /** The overall goal of the communication (e.g. "Book a call") */
  goalOfCommunication: string;

  /** The array of cases to handle different scenarios */
  caseHandling: AutoReplyCase[];

  /** Called when the user toggles the isActive switch. */
  onIsActiveChange(event: React.ChangeEvent<HTMLInputElement>): void;

  /** Called when user changes the goal text. Accepts a textfield event. */
  onGoalChange(event: React.ChangeEvent<HTMLInputElement>): void;

  /** Called when the user updates the entire array of cases. */
  onCaseHandlingUpdate(newCaseHandling: AutoReplyCase[]): void;

  /** Called to save or finalize the auto-reply settings. (Typically no event needed.) */
  handleUpdateAutoReply(): void;

  /* Added for placeholder validation */
  productGroup: ProductGroups;
  fieldInUse: { [key: string]: boolean };
  customPlaceholders: import('../../campaign-builder.types').CustomPlaceholder[];

  /**
   * Eingebettet (Kampagnen-Assistent): ohne eigenen Aktivierungsschalter (den setzt der Assistent), ohne „Speichern“
   * (der Assistent speichert selbst) und ohne unteren Rand. Fehlt die Angabe, verhält sich alles wie im alten Builder.
   */
  embedded?: boolean;
  /** Läuft vor dem Test der Antwort (der Test nutzt den gespeicherten Stand der Kampagne). */
  onBeforeTest?(): Promise<void>;
}

const AutoReplySettings: React.FC<AutoReplySettingsProps & { funnelId?: string }> = ({
  loading,
  isActive,
  goalOfCommunication,
  caseHandling,
  onIsActiveChange,
  onGoalChange,
  onCaseHandlingUpdate,
  handleUpdateAutoReply,
  funnelId,
  productGroup,
  fieldInUse,
  customPlaceholders,
  embedded = false,
  onBeforeTest,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { token } = useUserData();

  // Test Auto-Reply local state
  const [showTest, setShowTest] = useState(false);
  const [testTranscript, setTestTranscript] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any>(null);

  /* Snackbar for copy */
  const [copiedSnack, setCopiedSnack] = useState(false);
  const handleCopy = (token: string) => {
    navigator.clipboard.writeText(token).then(() => setCopiedSnack(true));
  };

  const getMissingTokens = (prompt: string): { std: string[]; custom: string[] } => {
    const missing: string[] = [];
    const fIn = fieldInUse;
    if (productGroup === ProductGroups.HR_BOOSTER) {
      if (fIn.jobOffer && !prompt.includes('[[{{jobOffer}}]]')) missing.push('[[{{jobOffer}}]]');
    } else {
      if (fIn.problems && !prompt.includes('[[{{problems}}]]')) missing.push('[[{{problems}}]]');
      if (fIn.features && !prompt.includes('{{features}}')) missing.push('{{features}}');
      if (fIn.benefits && !prompt.includes('{{benefits}}')) missing.push('{{benefits}}');
      if (fIn.impact && !prompt.includes('{{impact}}')) missing.push('{{impact}}');
    }

    // custom
    const missingCustom: string[] = [];
    if (customPlaceholders && customPlaceholders.length) {
      customPlaceholders.forEach((cp) => {
        const tok = `[[{{${cp.key}}}]]`;
        if (!prompt.includes(tok)) missingCustom.push(tok);
      });
    }
    return { std: missing, custom: missingCustom };
  };

  /**
   * Adds a new blank case so the user can fill it in.
   */
  function handleAddCase() {
    const newCase: AutoReplyCase = {
      promptToIdentify: '',
      promptToReply: '',
    };
    onCaseHandlingUpdate([...caseHandling, newCase]);
  }

  /**
   * Remove a case by index
   */
  function handleRemoveCase(idx: number) {
    const updated = [...caseHandling];
    updated.splice(idx, 1);
    onCaseHandlingUpdate(updated);
  }

  /**
   * Update a single field in a single case
   */
  function handleChangeField(idx: number, field: keyof AutoReplyCase, value: string) {
    const updated = [...caseHandling];
    updated[idx] = { ...updated[idx], [field]: value };
    onCaseHandlingUpdate(updated);
  }

  async function handleRunTest() {
    setTestLoading(true);
    setTestError(null);
    setTestResult(null);
    try {
      if (!token || !funnelId || !testTranscript.trim()) {
        setTestError('Missing required data.');
        setTestLoading(false);
        return;
      }
      if (onBeforeTest) await onBeforeTest();
      const res = await testAutoreply(funnelId, testTranscript);
      if (res && !res.error) {
        setTestResult(res);
      } else {
        setTestError(res?.error?.message || 'Unknown error');
      }
    } catch (e: any) {
      setTestError(e?.message || 'Unknown error');
    }
    setTestLoading(false);
  }

  const content = (
    <Stack className={embedded ? undefined : classes.autoReplySettings} gap={3}>
      {!embedded && (
        <>
          <Stack gap={2}>
            <Stack gap={1}>
              <Typography className={classes.subTitle}>{t('activateSetting')}</Typography>
              <Typography className={classes.text}>{t('autoReplyQuestion')}</Typography>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <Typography className={classes.text}>{t('no')}</Typography>
              <PaiSwitch checked={isActive} onChange={onIsActiveChange} />
              <Typography className={classes.text}>{t('yes')}</Typography>
            </Stack>
          </Stack>
          <Divider />
        </>
      )}
      <Stack gap={2}>
        <Stack gap={1}>
          <Typography className={classes.subTitle}>{t('goalOfCommunication')}</Typography>
          <Typography className={classes.text}>{t('goalOfCommunicationDescription')}</Typography>
        </Stack>
        <PaiTextField value={goalOfCommunication} placeholder={t('goalPlaceholder')} onChange={onGoalChange} />
      </Stack>
      <Divider />
      <Stack gap={2}>
        <Stack gap={1}>
          <Typography className={classes.subTitle}>{t('cases')}</Typography>
          <Typography className={classes.text}>{t('casesDescription')}</Typography>
        </Stack>
        {caseHandling.map((item, idx) => (
          <Stack key={idx} className={classes.autoReplyCase}>
            <Stack className={classes.caseHead} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <Typography className={classes.subTitle}>{t('caseNumber', { number: idx + 1 })}</Typography>
              <IconButton className={classes.iconButton} size="small" onClick={() => handleRemoveCase(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Stack className={classes.caseBody} gap={3}>
              <Stack gap={1}>
                <Typography className={classes.text}>{t('caseDescription')}</Typography>
                <PaiTextArea
                  placeholder={t('caseDescriptionPlaceholder')}
                  variant={'background'}
                  value={item.promptToIdentify}
                  rows={3}
                  onChange={(e) => handleChangeField(idx, 'promptToIdentify', e.target.value)}
                />
              </Stack>
              <Stack gap={1}>
                <Typography className={classes.text}>{t('casePrompt')}</Typography>
                <PaiTextArea
                  placeholder={t('casePromptPlaceholder')}
                  variant={'background'}
                  value={item.promptToReply}
                  rows={3}
                  onChange={(e) => handleChangeField(idx, 'promptToReply', e.target.value)}
                />
                {(() => {
                  const miss = getMissingTokens(item.promptToReply);
                  return (
                    <>
                      {miss.std.length > 0 && (
                        <Typography className={classes.errorText}>
                          {t('missingPlaceholdersWarningPrefix')}{' '}
                          {miss.std.map((tok, i) => (
                            <span
                              key={tok}
                              role="button"
                              tabIndex={0}
                              className={classes.copyToken}
                              onClick={() => handleCopy(tok)}
                              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleCopy(tok)}
                            >
                              {tok}
                              {i < miss.std.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </Typography>
                      )}
                      {miss.custom.length > 0 && (
                        <Typography className={classes.errorText}>
                          {t('missingCustomPlaceholdersWarningPrefix')}{' '}
                          {miss.custom.map((tok, i) => (
                            <span
                              key={tok}
                              role="button"
                              tabIndex={0}
                              className={classes.copyToken}
                              onClick={() => handleCopy(tok)}
                              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleCopy(tok)}
                            >
                              {tok}
                              {i < miss.custom.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </Stack>
            </Stack>
          </Stack>
        ))}
        <Stack direction={'row'} justifyContent={'flex-start'}>
          <PaiActionIconText variant={'add'} text={t('addCase')} action={handleAddCase} />
        </Stack>
      </Stack>
      <Stack direction={'row'} justifyContent={'flex-end'}>
        {!embedded && (
          <PaiButton text={t('saveAutoReplySettings')} size={'small'} border={'rounded'} action={handleUpdateAutoReply} loading={loading} />
        )}
        <Box ml={embedded ? 0 : 1.5} display="inline-block">
          <PaiButton
            text={showTest ? t('close') : t('testPrompt')}
            size={'small'}
            border={'rounded'}
            action={() => setShowTest((prev) => !prev)}
            color="secondary"
          />
        </Box>
      </Stack>
      {showTest && (
        <Stack gap={2} className={classes.testSection}>
          <Typography className={classes.subTitle}>{t('testAutoReplyTitle', 'Test Auto-Reply Function')}</Typography>
          <Typography className={classes.text}>
            {t(
              'testAutoReplyInstruction',
              'Copy and paste a LinkedIn conversation here to test the auto-reply function.'
            )}
          </Typography>
          <PaiTextArea
            value={testTranscript}
            onChange={(e) => setTestTranscript(e.target.value)}
            rows={8}
            placeholder={t('pasteLinkedinConversation', 'Paste LinkedIn conversation here...')}
            variant="background"
            disabled={testLoading}
          />
          <Stack direction="row" justifyContent="flex-end">
            <PaiButton
              text={t('testPrompt', 'Test')}
              size="small"
              border="rounded"
              action={handleRunTest}
              loading={testLoading}
              disabled={testLoading || !testTranscript.trim()}
            />
          </Stack>
          {testError && <Typography color="error">{testError}</Typography>}
          {testResult && (
            <Stack gap={2} className={classes.testResultSection}>
              <Typography className={classes.subTitle}>{t('testAutoReplyResult', 'Test Result')}</Typography>
              <Typography className={classes.text} style={{ fontWeight: 'bold' }}>
                {t('autoReplyGeneratedMessage', 'Generated Reply (will be sent):')}
              </Typography>
              <PaiTextArea
                value={testResult.generatedReply || ''}
                readOnly
                rows={6}
                variant="background"
              />
              <Typography className={classes.text} style={{ fontWeight: 'bold' }}>
                {t('autoReplyDetectedCase', 'Detected Case:')}
              </Typography>
              <PaiTextArea
                value={(() => {
                  const detectedPrompt = testResult?.detectedCase?.promptToReply;
                  const usedCase = Array.isArray(testResult?.usedCaseHandling)
                    ? testResult.usedCaseHandling.find((c: any) => c.promptToReply === detectedPrompt)
                    : null;
                  return usedCase?.promptToIdentify || '';
                })()}
                readOnly
                rows={3}
                variant="background"
              />
              {testResult?.detectedCase?.description || testResult?.caseDescription ? (
                <>
                  <Typography className={classes.text} style={{ fontWeight: 'bold' }}>
                    {t('autoReplyCaseExplanation', 'Case Detection Explanation:')}
                  </Typography>
                  <PaiTextArea
                    value={testResult?.detectedCase?.description || testResult?.caseDescription || ''}
                    readOnly
                    rows={3}
                    variant="background"
                  />
                </>
              ) : null}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );

  return (
    <>
      {content}
      <Snackbar
        open={copiedSnack}
        autoHideDuration={2000}
        message={t('placeholderCopied')}
        onClose={() => setCopiedSnack(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  autoReplySettings: {
    paddingBottom: '32px',
    marginBottom: '16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
  },
  text: {
    fontSize: theme.typography.pxToRem(14),
  },
  greyText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  autoReplyCase: {
    background: '#FFF',
    borderRadius: '10px',
  },
  caseHead: {
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  caseBody: {
    padding: '16px',
  },
  iconButton: {
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(16),
    },
  },
  testSection: {
    marginTop: '24px',
    padding: '16px',
    background: '#f7f9fc',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.greys.light}`,
  },
  testResultSection: {
    background: '#fff',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.greys.light}`,
    padding: '12px',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(13),
  },
  copyToken: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

export default AutoReplySettings;
